import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Module Paie & Social - Section "social" du dashboard.
 * Gestion salariés, calcul paie, génération bulletins PDF.
 *
 * Gate backend : PlanChecker(module="social", min_plan="pro")
 * → Si client en plan gratuit, erreur 403 remontée à l'utilisateur.
 */
const ModulePaie = ({ clientNom }) => {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedSalarie, setSelectedSalarie] = useState(null);
  const [calculResult, setCalculResult] = useState(null);
  const [moisCourant, setMoisCourant] = useState(new Date().toISOString().slice(0, 7));
  const [actionLoading, setActionLoading] = useState(null);

  // Form ajout salarié
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    type_contrat: 'CDI',
    statut_dirigeant: false,
    regime_social: 'general',
    salaire_brut: '',
    date_entree: new Date().toISOString().slice(0, 10),
  });

  const loadSalaries = async () => {
    setLoading(true);
    setError(null);
    const resp = await api.get('/social/salaries');
    if (resp && resp.success) {
      setSalaries(resp.data || []);
    } else if (resp && (resp.error || resp.detail)) {
      const msg = resp.detail || resp.error;
      setError(msg);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSalaries();
  }, []);

  // Ajouter un salarié
  const handleAddSalarie = async (e) => {
    e.preventDefault();
    setActionLoading('add');
    const resp = await api.post('/social/salaries', {
      ...formData,
      salaire_brut: parseFloat(formData.salaire_brut) || 0,
    });
    if (resp && resp.success) {
      setShowAddForm(false);
      setFormData({
        nom: '', prenom: '', type_contrat: 'CDI', statut_dirigeant: false,
        regime_social: 'general', salaire_brut: '', date_entree: new Date().toISOString().slice(0, 10),
      });
      await loadSalaries();
    } else {
      alert('❌ ' + ((resp && (resp.detail || resp.error)) || 'Erreur ajout salarié'));
    }
    setActionLoading(null);
  };

  // Supprimer salarié
  const handleDelete = async (salarieId, nom) => {
    if (!window.confirm(`Marquer ${nom} comme parti ? Son dossier sera conservé.`)) return;
    setActionLoading('delete-' + salarieId);
    const resp = await api.delete('/social/salaries/' + salarieId);
    if (resp && resp.success) {
      await loadSalaries();
      if (selectedSalarie && selectedSalarie.id === salarieId) setSelectedSalarie(null);
    }
    setActionLoading(null);
  };

  // Calcul paie
  const handleCalculer = async (salarie) => {
    setActionLoading('calcul-' + salarie.id);
    setCalculResult(null);
    const resp = await api.post('/paie/calculer', {
      brut: salarie.salaire_brut || 0,
      statut_cadre: !!salarie.statut_dirigeant,
      effectif: salaries.length,
    });
    if (resp && resp.success) {
      setCalculResult({ salarie, paie: resp.paie });
      setSelectedSalarie(salarie);
    } else {
      const msg = (resp && (resp.detail || resp.error)) || 'Erreur calcul';
      alert('❌ ' + msg + (msg.includes('plan') ? '\n\n💡 Améliorez vers le plan Pro pour accéder au module paie.' : ''));
    }
    setActionLoading(null);
  };

  // Télécharger bulletin PDF (single)
  const handleBulletinPDF = async (salarie) => {
    setActionLoading('pdf-' + salarie.id);
    try {
      const resp = await api.post('/paie/bulletin/' + salarie.id + '/' + moisCourant, null, { responseType: 'blob' });
      if (resp instanceof Blob) {
        const url = URL.createObjectURL(resp);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulletin_${salarie.nom}_${moisCourant}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        alert('❌ Erreur génération PDF');
      }
    } catch (e) {
      alert('❌ ' + e.message);
    }
    setActionLoading(null);
  };

  // Télécharger tous les bulletins ZIP
  const handleBatchZIP = async () => {
    setActionLoading('batch');
    try {
      const resp = await api.post('/paie/bulletins/batch/' + moisCourant, null, { responseType: 'blob' });
      if (resp instanceof Blob) {
        const url = URL.createObjectURL(resp);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulletins_${moisCourant}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      alert('❌ ' + e.message);
    }
    setActionLoading(null);
  };

  // Rendu
  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#73726C' }}>Chargement des salariés...</div>;
  }

  if (error && error.includes('403')) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Plan Pro requis</h2>
        <p style={{ color: '#73726C', marginBottom: '24px' }}>
          Le module Paie & Social est disponible à partir du plan Pro.
        </p>
        <p style={{ fontSize: '13px', color: '#73726C' }}>
          💡 Rendez-vous dans "💳 Mon Abonnement" pour améliorer votre plan.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>👥 Salariés & Paie</h2>
          <p style={{ fontSize: '13px', color: '#73726C', margin: '4px 0 0' }}>
            Gérez vos salariés, calculez les paies et générez les bulletins PDF conformes.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            type="month"
            value={moisCourant}
            onChange={(e) => setMoisCourant(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #E8E7E3',
              borderRadius: '8px',
              fontSize: '13px',
              background: 'white',
            }}
          />
          <button onClick={() => setShowAddForm(!showAddForm)} style={btnPrimary}>
            {showAddForm ? '← Annuler' : '➕ Ajouter salarié'}
          </button>
          {salaries.length > 0 && (
            <button onClick={handleBatchZIP} disabled={actionLoading === 'batch'} style={btnSecondary}>
              {actionLoading === 'batch' ? '⏳ Génération...' : '📦 Tous les bulletins (ZIP)'}
            </button>
          )}
        </div>
      </div>

      {/* Form ajout */}
      {showAddForm && (
        <form
          onSubmit={handleAddSalarie}
          style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #E8E7E3',
            marginBottom: '20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
          }}
        >
          <input required placeholder="Nom" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} style={inputStyle} />
          <input required placeholder="Prénom" value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} style={inputStyle} />
          <select value={formData.type_contrat} onChange={(e) => setFormData({ ...formData, type_contrat: e.target.value })} style={inputStyle}>
            <option value="CDI">CDI</option>
            <option value="CDD">CDD</option>
            <option value="Alternance">Alternance</option>
            <option value="dirigeant_salarie">Dirigeant assimilé (SAS)</option>
          </select>
          <input type="number" required min="0" step="0.01" placeholder="Salaire brut mensuel (€)" value={formData.salaire_brut} onChange={(e) => setFormData({ ...formData, salaire_brut: e.target.value })} style={inputStyle} />
          <input type="date" value={formData.date_entree} onChange={(e) => setFormData({ ...formData, date_entree: e.target.value })} style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={formData.statut_dirigeant}
              onChange={(e) => setFormData({ ...formData, statut_dirigeant: e.target.checked })}
            />
            Cadre / Dirigeant
          </label>
          <button type="submit" disabled={actionLoading === 'add'} style={{ ...btnPrimary, gridColumn: '1 / -1', justifySelf: 'start' }}>
            {actionLoading === 'add' ? '⏳ Ajout...' : '✅ Enregistrer'}
          </button>
        </form>
      )}

      {/* Liste salariés */}
      {salaries.length === 0 ? (
        <div style={{ background: 'white', padding: '40px', borderRadius: '12px', border: '1px solid #E8E7E3', textAlign: 'center', color: '#73726C' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
          <p>Aucun salarié enregistré.</p>
          <p style={{ fontSize: '13px' }}>Cliquez sur "Ajouter salarié" pour commencer.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          {salaries.map((s) => (
            <div
              key={s.id}
              style={{
                background: 'white',
                padding: '16px',
                borderRadius: '12px',
                border: selectedSalarie && selectedSalarie.id === s.id ? '2px solid #1D9E75' : '1px solid #E8E7E3',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '15px' }}>{s.prenom} {s.nom}</div>
                  <div style={{ fontSize: '12px', color: '#73726C' }}>{s.type_contrat}{s.statut_dirigeant ? ' · Cadre' : ''}</div>
                </div>
                <button
                  onClick={() => handleDelete(s.id, `${s.prenom} ${s.nom}`)}
                  disabled={actionLoading === 'delete-' + s.id}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#D85A30',
                    cursor: 'pointer',
                    fontSize: '18px',
                  }}
                  title="Marquer comme parti"
                >
                  ×
                </button>
              </div>

              <div style={{ fontSize: '13px', color: '#2D2D2D', margin: '8px 0' }}>
                Brut : <strong>{(s.salaire_brut || 0).toFixed(2).replace('.', ',')}€</strong>/mois
              </div>
              <div style={{ fontSize: '12px', color: '#73726C', marginBottom: '12px' }}>
                Net estimé : <strong>{(s.salaire_net_estime || 0).toFixed(2).replace('.', ',')}€</strong>
              </div>

              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button onClick={() => handleCalculer(s)} disabled={actionLoading === 'calcul-' + s.id} style={btnSmallPrimary}>
                  {actionLoading === 'calcul-' + s.id ? '⏳' : '🧮 Calculer'}
                </button>
                <button onClick={() => handleBulletinPDF(s)} disabled={actionLoading === 'pdf-' + s.id} style={btnSmallSecondary}>
                  {actionLoading === 'pdf-' + s.id ? '⏳' : '📄 Bulletin'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Panneau détail calcul paie */}
      {calculResult && selectedSalarie && (
        <div
          style={{
            position: 'fixed',
            right: '20px',
            bottom: '20px',
            width: '420px',
            maxWidth: 'calc(100vw - 40px)',
            background: 'white',
            borderRadius: '16px',
            border: '1px solid #E8E7E3',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '20px',
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
              💶 Paie de {selectedSalarie.prenom} {selectedSalarie.nom}
            </h3>
            <button onClick={() => setCalculResult(null)} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#73726C' }}>×</button>
          </div>

          <div style={{ fontSize: '13px', color: '#2D2D2D' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F5F4ED' }}>
              <span>Salaire brut</span>
              <strong>{calculResult.paie.brut.toFixed(2).replace('.', ',')}€</strong>
            </div>

            <div style={{ fontSize: '11px', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>
              Cotisations salariales ({calculResult.paie.cotisations_salariales.length})
            </div>
            {calculResult.paie.cotisations_salariales.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#73726C' }}>
                <span>{c.libelle}</span>
                <span>-{c.montant.toFixed(2).replace('.', ',')}€</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', padding: '4px 0', marginTop: '4px' }}>
              <span>Total salariales</span>
              <span>-{calculResult.paie.totaux.total_cotisations_salariales.toFixed(2).replace('.', ',')}€</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px', borderTop: '2px solid #1D9E75', fontSize: '16px', fontWeight: '700', color: '#1D9E75' }}>
              <span>NET À PAYER</span>
              <span>{calculResult.paie.totaux.salaire_net_a_payer.toFixed(2).replace('.', ',')}€</span>
            </div>

            <div style={{ fontSize: '11px', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>
              Charges patronales
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#73726C' }}>
              <span>Total (net Fillon)</span>
              <span>+{calculResult.paie.totaux.total_cotisations_patronales_net.toFixed(2).replace('.', ',')}€</span>
            </div>
            {calculResult.paie.allegement_fillon.eligible && (
              <div style={{ fontSize: '11px', color: '#1D9E75', marginTop: '4px' }}>
                ✅ Allègement Fillon : -{calculResult.paie.allegement_fillon.montant.toFixed(2).replace('.', ',')}€
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', padding: '4px 0', marginTop: '4px', color: '#2D2D2D' }}>
              <span>Coût total employeur</span>
              <span>{calculResult.paie.totaux.cout_total_employeur.toFixed(2).replace('.', ',')}€</span>
            </div>

            <button
              onClick={() => handleBulletinPDF(selectedSalarie)}
              disabled={actionLoading === 'pdf-' + selectedSalarie.id}
              style={{ ...btnPrimary, width: '100%', marginTop: '12px' }}
            >
              {actionLoading === 'pdf-' + selectedSalarie.id ? '⏳ Génération PDF...' : '📄 Télécharger bulletin ' + moisCourant}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const btnPrimary = {
  padding: '8px 16px',
  background: '#1D9E75',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
};

const btnSecondary = {
  padding: '8px 16px',
  background: 'white',
  color: '#0F6E56',
  border: '1px solid #1D9E75',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
};

const btnSmallPrimary = {
  padding: '6px 10px',
  background: '#1D9E75',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};

const btnSmallSecondary = {
  padding: '6px 10px',
  background: 'white',
  color: '#0F6E56',
  border: '1px solid #1D9E75',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};

const inputStyle = {
  padding: '8px 12px',
  border: '1px solid #E8E7E3',
  borderRadius: '8px',
  fontSize: '13px',
  background: 'white',
};

export default ModulePaie;
